import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, Typography } from '@material-ui/core';
import styled from 'styled-components';
import {
  AddRounded as AddIcon,
  CheckCircleRounded as ApprovedIcon,
  RemoveCircleRounded as RetractedIcon,
  FileCopyRounded as DuplicateIcon,
  EditRounded as EditIcon,
  CancelRounded as RejectedIcon,
  HourglassEmptyRounded as PendingIcon,
  ErrorRounded as FailedIcon
} from '@material-ui/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Alert } from '@material-ui/lab';
import { theme } from 'theme';
import { isOrgReadOnly, useSpot, useStoredStringState } from 'framework';
import { ExpenseClaim, LineItem, Org } from 'domain-types';
import { getExpenseClaimDisplayDate } from 'screens/expense-claim-date';
import {
  Row,
  Spacer,
  Table,
  ConfirmDialog,
  CreateExpenseClaimDialog,
  CreateExpenseClaimResult
} from 'components';

const ApprovedIconStyled = styled(ApprovedIcon)`
  color: ${theme.palette.success.main};
`;
const RejectedIconStyled = styled(RejectedIcon)`
  color: ${theme.palette.error.main};
`;
const FailedIconStyled = styled(FailedIcon)`
  color: ${theme.palette.warning.main};
`;
const RetractedIconStyled = styled(RetractedIcon)`
  color: ${theme.palette.grey[600]};
`;
const NeutralEditIconStyled = styled(EditIcon)`
  color: ${theme.palette.text.disabled};
`;
const NeutralPendingIconStyled = styled(PendingIcon)`
  color: ${theme.palette.text.disabled};
`;

const ApprovedChip = styled(Chip)`
  background-color: ${theme.palette.success.main}22;
`;
const DraftChip = styled(Chip)`
  background-color: ${theme.palette.background.default};
`;
const RejectedChip = styled(Chip)`
  background-color: ${theme.palette.error.main}22;
`;
const FailedChip = styled(Chip)`
  background-color: ${theme.palette.warning.main}22;
`;
const RetractedChip = styled(Chip)`
  background-color: ${theme.palette.grey[300]};
`;
const PendingChip = styled(Chip)`
  background-color: ${theme.palette.grey[100]};
`;

interface ConfirmDialogDesc {
  title: string;
  text: string;
  callback: () => Promise<void>;
}

const getClaimStatusIcon = (status: string) => {
  switch (status) {
    case 'approved_sent':
      return <ApprovedIconStyled />;
    case 'draft':
      return <NeutralEditIconStyled />;
    case 'rejected':
      return <RejectedIconStyled />;
    case 'retracted':
      return <RetractedIconStyled />;
    case 'send_failed':
      return <FailedIconStyled />;
    default:
      return <NeutralPendingIconStyled />;
  }
};

const getStatusChip = (status: string, t: (key: string) => string) => {
  switch (status) {
    case 'approved_sent':
      return <ApprovedChip size="small" label={t('claimApprovedSent')} />;
    case 'draft':
      return <DraftChip size="small" label={t('claimDraft')} />;
    case 'rejected':
      return <RejectedChip size="small" label={t('claimRejected')} />;
    case 'retracted':
      return <RetractedChip size="small" label={t('claimRetracted')} />;
    case 'send_failed':
      return <FailedChip size="small" label={t('claimSendFailed')} />;
    default:
      return <PendingChip size="small" label={t('claimPendingApproval')} />;
  }
};

export function Expenses({
  id,
  organization
}: {
  id: string;
  organization: string;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { data, command, query } = useSpot();
  const [storedIban, setStoredIban] = useStoredStringState(
    `spothr.expenses.iban.${organization}.${id}`,
    ''
  );

  const org = data?.organizations?.[organization] as Org | undefined;
  const readOnly = isOrgReadOnly(org);
  const claims: ExpenseClaim[] = (data?.expenses?.[id] as ExpenseClaim[]) ?? [];

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [dialogClaimId, setDialogClaimId] = useState<string | undefined>();
  const [dialogIban, setDialogIban] = useState<string | undefined>();
  const [draftLineItems, setDraftLineItems] = useState<
    LineItem[] | undefined
  >();
  const [draftReceiptFiles, setDraftReceiptFiles] = useState<
    Array<{ key: string; name: string }> | undefined
  >();
  const [editingDraft, setEditingDraft] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogDesc | null>(
    null
  );

  const refreshClaims = useCallback(async () => {
    await query(`org/${organization}/user/${id}/expenses`, {}, [
      'expenses',
      id
    ]);
  }, [organization, id, query]);

  useEffect(() => {
    (async () => {
      if (!data?.users?.[id]) {
        await query(`org/${organization}/user/${id}`, {}, ['users', id]);
      }
      if (!data?.organizations?.[organization]) {
        await query(`org/${organization}`, {}, ['organizations', organization]);
      }
      await refreshClaims();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetDialogState = () => {
    setDialogClaimId(undefined);
    setDialogIban(undefined);
    setDraftLineItems(undefined);
    setDraftReceiptFiles(undefined);
    setEditingDraft(false);
    setCreateDialogOpen(false);
  };

  const onCreateClaim = async (result: CreateExpenseClaimResult) => {
    if (readOnly) {
      return;
    }

    await command(
      dialogClaimId
        ? `org/${organization}/user/${id}/expenses/${encodeURIComponent(
            dialogClaimId
          )}/submit`
        : `org/${organization}/user/${id}/expenses`,
      {
        iban: result.iban,
        lineItems: result.lineItems,
        receiptFiles: result.receiptFiles,
        existingReceiptFileKeys: result.existingReceiptFileKeys
      }
    );
    setStoredIban(result.iban);
    await refreshClaims();
    resetDialogState();
  };

  const onSaveDraft = async (result: CreateExpenseClaimResult) => {
    if (readOnly) {
      return;
    }

    await command(
      dialogClaimId
        ? `org/${organization}/user/${id}/expenses/${encodeURIComponent(
            dialogClaimId
          )}/draft`
        : `org/${organization}/user/${id}/expenses/draft`,
      {
        iban: result.iban,
        lineItems: result.lineItems,
        receiptFiles: result.receiptFiles,
        existingReceiptFileKeys: result.existingReceiptFileKeys
      }
    );
    if (result.iban) {
      setStoredIban(result.iban);
    }
    await refreshClaims();
    resetDialogState();
  };

  const totalFormatted = (claim: ExpenseClaim) =>
    `€${claim.totalAmountIncl.toFixed(2)}`;
  const canRetractClaim = (claim: ExpenseClaim) =>
    ['pending_approval', 'approved_sent', 'send_failed'].includes(claim.status);

  const openNewClaimDialog = () => {
    if (readOnly) {
      return;
    }

    setDialogClaimId(undefined);
    setDialogIban(undefined);
    setDraftLineItems(undefined);
    setDraftReceiptFiles(undefined);
    setEditingDraft(false);
    setCreateDialogOpen(true);
  };

  const duplicateClaim = (claim: ExpenseClaim) => {
    if (readOnly) {
      return;
    }

    setDialogClaimId(undefined);
    setDialogIban(undefined);
    setDraftLineItems(claim.lineItems);
    setDraftReceiptFiles(undefined);
    setEditingDraft(false);
    setCreateDialogOpen(true);
  };

  const editDraftClaim = (claim: ExpenseClaim) => {
    if (readOnly) {
      return;
    }

    setDialogClaimId(claim.id);
    setDialogIban(claim.iban ?? storedIban);
    setDraftLineItems(claim.lineItems);
    setDraftReceiptFiles(
      claim.receiptFileKeys.map(key => ({
        key,
        name: key.split('/').pop() ?? key
      }))
    );
    setEditingDraft(true);
    setCreateDialogOpen(true);
  };

  const sortedClaims = [...claims].sort((a, b) =>
    getExpenseClaimDisplayDate(b).localeCompare(getExpenseClaimDisplayDate(a))
  );

  return (
    <>
      {readOnly ? (
        <Alert severity="warning">{t('trialReadOnlyBanner')}</Alert>
      ) : null}
      <Row mb={1}>
        <Spacer />
        <Button
          color="primary"
          startIcon={<AddIcon />}
          aria-label="add"
          onClick={openNewClaimDialog}
          disabled={readOnly}
        >
          {t('newExpenseClaim')}
        </Button>
      </Row>
      <Table
        maxHeight="580px"
        headers={[
          t('status'),
          t('date'),
          t('total'),
          t('iban'),
          { id: 'claim-status-label', content: String(t('status')) },
          {
            id: 'claim-duplicate-action',
            content: String(t('duplicateExpenseClaim'))
          },
          {
            id: 'claim-retract-action',
            content: String(t('retractExpenseClaim'))
          }
        ]}
        rows={sortedClaims.map(claim => ({
          id: claim.id,
          disabled: false,
          cells: [
            <Box key="status" title={t(claim.status)}>
              {getClaimStatusIcon(claim.status)}
            </Box>,
            moment(getExpenseClaimDisplayDate(claim)).format('LL'),
            <Typography key="total" variant="body2">
              {totalFormatted(claim)}
            </Typography>,
            <Typography key="iban" variant="body2">
              {claim.ibanLast4 ? `****${claim.ibanLast4}` : '-'}
            </Typography>,
            getStatusChip(claim.status, t),
            <Button
              key="duplicate"
              size="small"
              color="primary"
              startIcon={
                claim.status === 'draft' ? (
                  <EditIcon fontSize="small" />
                ) : (
                  <DuplicateIcon fontSize="small" />
                )
              }
              onClick={event => {
                event.stopPropagation();
                if (claim.status === 'draft') {
                  editDraftClaim(claim);
                } else {
                  duplicateClaim(claim);
                }
              }}
              disabled={readOnly}
              aria-label={
                claim.status === 'draft'
                  ? t('editExpenseClaim')
                  : t('duplicateExpenseClaim')
              }
            >
              {claim.status === 'draft'
                ? t('editExpenseClaim')
                : t('duplicateExpenseClaim')}
            </Button>,
            canRetractClaim(claim) ? (
              <Button
                key="retract"
                size="small"
                color="primary"
                onClick={event => {
                  event.stopPropagation();
                  setConfirmDialog({
                    title: t('retractExpenseTitle'),
                    text: t('retractExpenseText'),
                    callback: async () => {
                      await command(
                        `org/${organization}/user/${id}/expenses/${encodeURIComponent(
                          claim.id
                        )}/retract`,
                        {}
                      );
                      await refreshClaims();
                    }
                  });
                }}
                disabled={readOnly}
                aria-label={t('retractExpenseClaim')}
              >
                {t('retractExpenseClaim')}
              </Button>
            ) : null
          ]
        }))}
        onRowClick={row => {
          const claim = sortedClaims.find(item => item.id === row.id);
          if (claim?.status === 'draft') {
            if (readOnly) {
              return;
            }
            editDraftClaim(claim);
            return;
          }
          history.push(
            `/${organization}/expenses/${encodeURIComponent(row.id)}`
          );
        }}
      />
      <CreateExpenseClaimDialog
        dialogOpen={createDialogOpen}
        initialIban={dialogIban ?? storedIban}
        initialLineItems={draftLineItems}
        initialReceiptFiles={draftReceiptFiles}
        defaultKmRate={org?.expenseKmRate ?? 0.23}
        title={editingDraft ? t('editExpenseClaim') : t('newExpenseClaim')}
        onClose={resetDialogState}
        onSubmit={onCreateClaim}
        onSaveDraft={onSaveDraft}
      />
      <ConfirmDialog
        title={confirmDialog?.title ?? ''}
        text={confirmDialog?.text ?? ''}
        dialogOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        onSubmit={confirmDialog?.callback}
      />
    </>
  );
}
